import { useCallback, useEffect, useRef, useState } from "react";

import apiService from "../services/api";
import {
  buildClientActivityGeoJson,
  extractPlayerUsers,
  normalizeGeoJsonResponse,
} from "../utils/clientActivityGeo";

const POLL_INTERVAL_MS = 30000;
const EMPTY_GEOJSON = {
  type: "FeatureCollection",
  features: [],
};

export function useClientActivityGeo(enabled) {
  const [geoJson, setGeoJson] = useState(EMPTY_GEOJSON);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const [source, setSource] = useState("idle");
  const isMountedRef = useRef(true);

  const fetchGeoJson = useCallback(async () => {
    if (!enabled) {
      setGeoJson(EMPTY_GEOJSON);
      setLoading(false);
      setSource("idle");

      return;
    }

    try {
      let normalized = null;

      try {
        const geoResponse = await apiService.getClientActivityGeo();

        normalized = normalizeGeoJsonResponse(geoResponse);
      } catch (geoError) {
        // Geo endpoint may not be deployed yet; fall back to derived map data.
        console.warn("Client activity geo endpoint unavailable", geoError);
      }

      if (normalized) {
        if (!isMountedRef.current) return;
        setGeoJson(normalized);
        setError(null);
        setSource("api");

        return;
      }

      const [activityResponse, playerUsersResponse] = await Promise.all([
        apiService.getClientActivity(),
        apiService.getPlayerUsers({ live: true }).catch(() => null),
      ]);

      const activityData = activityResponse?.data || {};
      const recentActivity = Array.isArray(activityData.recentActivity)
        ? activityData.recentActivity
        : [];
      const playerUsers = extractPlayerUsers(playerUsersResponse);

      // Prefer already-geocoded payloads. Client-side IP lookup is a last resort
      // and is rate-limited/cached inside lookupIp to avoid ipapi 429 storms.
      const built = await buildClientActivityGeoJson({
        recentActivity,
        playerUsers,
        liveOnly: true,
      });

      if (!isMountedRef.current) return;

      setGeoJson(built);
      setError(null);
      setSource(built.features.length ? "derived" : "empty");
    } catch (err) {
      if (!isMountedRef.current) return;
      console.warn("Failed to load client activity map data", err);
      setError(err);
      setGeoJson(EMPTY_GEOJSON);
      setSource("error");
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      setLoading(false);

      return undefined;
    }

    setLoading(true);
    fetchGeoJson();

    const intervalId = setInterval(fetchGeoJson, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [enabled, fetchGeoJson]);

  return {
    geoJson,
    loading,
    error,
    source,
    refresh: fetchGeoJson,
  };
}
